// src/components/practice/markSheetView.js
//
// Pure field selection for the mark sheet (lesson-3c-marksheet). Turns a graded
// result -- live from gradeEssay, or the persisted aiFeedback subset from My Essays --
// into exactly what the header renders, and nothing more.
//
// THE GRADING CONTRACT IS NOT TOUCHED. This is presentation only: it reads
// indicativeBandRange / mostLikelyBand / aoMarks / totalMark / totalMax and derives
// display strings. It never invents a field the server did not send.
//
// In particular: `criteria` entries carry a name, a comment and evidence -- and NO
// pass/fail status. There is deliberately no helper here that scores a criterion,
// because the data cannot support one, and a tick or a warning triangle beside a
// criterion would be a value the model never produced.

// "Grade 7" / "Level 4" -> { vocab: 'Grade'|'Level', n } ; anything else -> null.
// Both vocabularies are live: marks mode and the current band vocab stamp "Grade N",
// while older records may still hold the legacy "Level N".
import { feedbackText } from '../../utils/feedbackText.js';

function parseBand(s) {
    if (typeof s !== 'string') return null;
    const m = /^(grade|level)\s+(\d+)$/i.exec(s.trim());
    if (!m) return null;
    const n = parseInt(m[2], 10);
    if (!Number.isInteger(n)) return null;
    return { vocab: m[1].charAt(0).toUpperCase() + m[1].slice(1).toLowerCase(), n };
}

// "Grade 6" + "Grade 8" -> "Grade 6-8"; equal ends -> the single band; a pair the
// regex cannot read (or a mixed vocabulary) -> "low - high" verbatim, so an unusual
// band name still displays rather than vanishing.
export function compactRange(range) {
    const r = range || {};
    const low = typeof r.low === 'string' ? r.low.trim() : '';
    const high = typeof r.high === 'string' ? r.high.trim() : '';
    if (!low && !high) return '';
    if (!low || !high) return low || high;
    if (low === high) return low;
    const a = parseBand(low);
    const b = parseBand(high);
    if (a && b && a.vocab === b.vocab) return a.vocab + ' ' + a.n + '-' + b.n;
    return low + ' - ' + high;
}

// Everything the mark-sheet HEADER shows.
//
//   grade    -- the chip: the most likely band when the server gave one, else the
//               compacted range. '' when the record carries no band at all (the chip
//               is then not rendered -- never a placeholder dash).
//   gradeSub -- the qualifying line under it, or '' when it would only repeat the
//               chip (a single-band range whose most-likely equals it).
//   bars     -- one entry per assessment objective, ONLY in marks mode. Band-only
//               records get [] and the sheet shows no bars rather than fabricated ones.
//   totalMark / totalMax -- marks mode only; null otherwise.
export function markSheetView(fb) {
    const f = fb || {};
    const range = f.indicativeBandRange || {};
    const compact = compactRange(range);
    const most = (typeof f.mostLikelyBand === 'string' && f.mostLikelyBand.trim()) ? f.mostLikelyBand.trim() : '';

    let grade = most || compact;
    let gradeSub = '';
    if (most && compact && compact !== most) gradeSub = 'most likely · range ' + compact;
    else if (!most && compact && range.low !== range.high && range.low && range.high) gradeSub = 'indicative range';

    // Marks mode is an ALL-OR-NOTHING shape: every aoMarks entry must carry integer
    // mark/maxMark/level AND the totals must be integers. A half-populated record
    // falls back to band-only rather than drawing a bar from a partial number.
    const aoOk = Array.isArray(f.aoMarks) && f.aoMarks.length > 0
        && f.aoMarks.every((a) => a && Number.isInteger(a.mark) && Number.isInteger(a.maxMark)
            && a.maxMark > 0 && Number.isInteger(a.level))
        && Number.isInteger(f.totalMark) && Number.isInteger(f.totalMax);

    const bars = aoOk ? f.aoMarks.map((a) => ({
        key: a.key || a.label || 'AO',
        label: a.label || a.key || 'AO',
        mark: a.mark,
        maxMark: a.maxMark,
        level: a.level,
        // Clamped: a mark above its max would otherwise overflow the track.
        pct: Math.max(0, Math.min(100, Math.round((a.mark / a.maxMark) * 100))),
    })) : [];

    return {
        grade,
        gradeSub,
        bars,
        totalMark: aoOk ? f.totalMark : null,
        totalMax: aoOk ? f.totalMax : null,
    };
}

// The criteria the sheet renders as cards. Kept as a helper so the "no status is
// invented" rule lives in one tested place: each row is exactly name / comment /
// evidence, and a row with nothing to say is dropped rather than rendered empty.
export function markSheetCriteria(fb) {
    const list = (fb && Array.isArray(fb.criteria)) ? fb.criteria : [];
    return list
        .map((c) => ({
            name: feedbackText(c && c.name),
            comment: feedbackText(c && c.comment),
            evidence: feedbackText(c && c.evidenceFromEssay),
        }))
        .filter((c) => c.name || c.comment || c.evidence);
}

// The improvements, as the checklist's items. The CF bounds this at 3, which is where
// "your next 3 moves" comes from; the cap is re-applied here so a longer legacy record
// cannot stretch the heading into a lie.
export const MAX_MOVES = 3;

export function markSheetMoves(fb) {
    const list = (fb && Array.isArray(fb.improvements)) ? fb.improvements : [];
    return list
        .map(feedbackText)
        .filter(Boolean)
        .slice(0, MAX_MOVES);
}
